import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiFetch, ApiHttpError } from '@/lib/api'
import type { AdminWorkSession } from './use-admin-sessions'

const ADMIN_SESSIONS_KEY = ['admin-sessions'] as const
const ADMIN_SESSION_DETAILS_KEY = ['admin-session'] as const

export interface UpdateSessionInput {
  id: string
  startedAt?: string
  endedAt?: string | null
  notes?: string | null
}

export function useUpdateSession() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateSessionInput) =>
      apiFetch<{ session: AdminWorkSession }>(
        `/api/admin/work-sessions/${id}`,
        { method: 'PATCH', body: { ...patch } },
      ),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ADMIN_SESSIONS_KEY })
      void qc.invalidateQueries({
        queryKey: [...ADMIN_SESSION_DETAILS_KEY, variables.id],
      })
    },
  })
}

export function useDeleteSession() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<null>(`/api/admin/work-sessions/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMIN_SESSIONS_KEY })
    },
  })
}

export interface CreateManualSessionInput {
  userId: string
  startedAt: string
  endedAt?: string
  notes?: string
}

export interface ManualSessionCollisionError {
  status: 409
  collisions: AdminWorkSession[]
  message: string
}

export function useCreateManualSession() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateManualSessionInput) =>
      apiFetch<{ session: AdminWorkSession }>('/api/admin/work-sessions', {
        method: 'POST',
        body: { ...body },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMIN_SESSIONS_KEY })
    },
  })
}

export function extractCollisions(err: unknown): AdminWorkSession[] | null {
  if (!(err instanceof ApiHttpError) || err.status !== 409) return null
  const body = err.body
  if (typeof body !== 'object' || body === null) return null
  const cols = (body as Record<string, unknown>).collisions
  if (!Array.isArray(cols)) return null
  return cols as AdminWorkSession[]
}

export interface AdminAuditEntry {
  id: string
  actorId: string
  actor?: { id: string; name: string; email: string }
  action: string
  targetType: string
  targetId: string
  beforeJson: Record<string, unknown> | null
  afterJson: Record<string, unknown> | null
  createdAt: string
}

export function useSessionDetails(id: string | null) {
  return useQuery({
    queryKey: [...ADMIN_SESSION_DETAILS_KEY, id],
    queryFn: () =>
      apiFetch<{
        session: AdminWorkSession
        auditTrail: AdminAuditEntry[]
      }>(`/api/admin/work-sessions/${id}`),
    enabled: Boolean(id),
    staleTime: 10_000,
  })
}
